import type { AnalysisData, KLinePoint } from "@/lifekline/types";
import { 
  User, 
  Briefcase, 
  Coins, 
  Heart, 
  Activity, 
  Users, 
  Compass, 
  Bitcoin,
  Star
} from "lucide-react";

interface AnalysisResultProps {
  analysis: AnalysisData;
  chartData: KLinePoint[];
  userName?: string;
}

interface AnalysisCardProps {
  title: string;
  icon: React.ReactNode;
  content: string;
  score: number;
  colorClass: string;
}

// Score bar component
function ScoreBar({ score, colorClass }: { score: number; colorClass: string }) {
  const percentage = score * 10;
  
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClass} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-sm font-semibold w-8">{score}/10</span>
    </div>
  );
}

// Analysis card component
function AnalysisCard({ title, icon, content, score, colorClass }: AnalysisCardProps) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 space-y-3 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2">
        <div className={`p-2 rounded-lg ${colorClass} bg-opacity-10`}>
          {icon}
        </div>
        <h4 className="font-semibold text-foreground">{title}</h4>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>
      <ScoreBar score={score} colorClass={colorClass} />
    </div>
  );
}

// Score legend component
function ScoreLegend() {
  const levels = [
    { range: "0-2分", label: "极差", color: "bg-red-500" },
    { range: "3-4分", label: "差", color: "bg-orange-500" },
    { range: "5-6分", label: "一般", color: "bg-yellow-500" },
    { range: "7-8分", label: "好", color: "bg-green-500" },
    { range: "9-10分", label: "极好", color: "bg-emerald-500" },
  ];
  
  return (
    <div className="flex flex-wrap gap-4 text-sm">
      {levels.map(({ range, label, color }) => (
        <div key={range} className="flex items-center gap-1">
          <span className={`w-3 h-3 rounded ${color}`} />
          <span className="text-muted-foreground">{range} {label}</span>
        </div>
      ))}
    </div>
  );
}

export function AnalysisResult({ analysis, chartData, userName }: AnalysisResultProps) {
  // Get peak and low years from chart data
  const peakYear = chartData.reduce((max, p) => p.high > (max?.high || 0) ? p : max, chartData[0]);
  const lowYear = chartData.reduce((min, p) => p.low < (min?.low || 100) ? p : min, chartData[0]);
  
  return (
    <div className="space-y-8">
      {/* Summary Section */}
      <section className="bg-gradient-to-br from-violet-500/10 via-purple-500/10 to-fuchsia-500/10 rounded-2xl p-6 border border-violet-200 dark:border-violet-900">
        <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
          <Star className="w-6 h-6 text-amber-500" />
          {userName ? `${userName}的` : ""}命盘总评
        </h3>
        <p className="text-foreground leading-relaxed mb-4">{analysis.summary}</p>
        <ScoreBar score={analysis.summaryScore} colorClass="bg-violet-500" />
        
        {/* Bazi Display */}
        {analysis.bazi && analysis.bazi.length === 4 && (
          <div className="mt-4 pt-4 border-t border-violet-200 dark:border-violet-800">
            <div className="text-sm text-muted-foreground mb-2">八字四柱</div>
            <div className="grid grid-cols-4 gap-2 text-center">
              {["年柱", "月柱", "日柱", "时柱"].map((label, index) => (
                <div key={label} className="bg-background/50 rounded-lg p-2">
                  <div className="text-xs text-muted-foreground">{label}</div>
                  <div className="font-bold text-lg text-foreground">{analysis.bazi[index]}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Key Years */}
        <div className="mt-4 pt-4 border-t border-violet-200 dark:border-violet-800 grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-green-500/10 rounded-lg">
            <div className="text-sm text-muted-foreground">人生巅峰</div>
            <div className="font-bold text-green-600">{peakYear?.year}年 {peakYear?.ganZhi}</div>
            <div className="text-xs text-muted-foreground">{peakYear?.age}岁 · 评分 {peakYear?.high}</div>
          </div>
          <div className="text-center p-3 bg-red-500/10 rounded-lg">
            <div className="text-sm text-muted-foreground">最低谷</div>
            <div className="font-bold text-red-600">{lowYear?.year}年 {lowYear?.ganZhi}</div>
            <div className="text-xs text-muted-foreground">{lowYear?.age}岁 · 评分 {lowYear?.low}</div>
          </div>
        </div>
      </section>
      
      {/* Main Analysis Cards */}
      <section>
        <h3 className="text-lg font-semibold mb-4">命理详批</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <AnalysisCard
            title="性格分析"
            icon={<User className="w-5 h-5 text-violet-500" />}
            content={analysis.personality}
            score={analysis.personalityScore}
            colorClass="bg-violet-500"
          />
          <AnalysisCard
            title="事业分析"
            icon={<Briefcase className="w-5 h-5 text-blue-500" />}
            content={analysis.industry}
            score={analysis.industryScore}
            colorClass="bg-blue-500"
          />
          <AnalysisCard
            title="财富分析"
            icon={<Coins className="w-5 h-5 text-amber-500" />}
            content={analysis.wealth}
            score={analysis.wealthScore}
            colorClass="bg-amber-500"
          />
          <AnalysisCard
            title="婚姻分析"
            icon={<Heart className="w-5 h-5 text-pink-500" />}
            content={analysis.marriage}
            score={analysis.marriageScore}
            colorClass="bg-pink-500"
          />
          <AnalysisCard
            title="健康分析"
            icon={<Activity className="w-5 h-5 text-green-500" />}
            content={analysis.health}
            score={analysis.healthScore}
            colorClass="bg-green-500"
          />
          <AnalysisCard
            title="六亲分析"
            icon={<Users className="w-5 h-5 text-cyan-500" />}
            content={analysis.family}
            score={analysis.familyScore}
            colorClass="bg-cyan-500"
          />
        </div>
      </section>
      
      {/* FengShui Section */}
      <section className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl p-6 border border-emerald-200 dark:border-emerald-900">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Compass className="w-5 h-5 text-emerald-500" />
          发展风水建议
        </h3>
        <p className="text-foreground leading-relaxed mb-4">{analysis.fengShui}</p>
        <ScoreBar score={analysis.fengShuiScore} colorClass="bg-emerald-500" />
      </section>
      
      {/* Crypto Section */}
      <section className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 rounded-2xl p-6 border border-orange-200 dark:border-orange-900">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Bitcoin className="w-5 h-5 text-orange-500" />
          币圈交易运势
        </h3>
        <p className="text-foreground leading-relaxed mb-4">{analysis.crypto}</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div className="bg-background/50 rounded-lg p-3 text-center">
            <div className="text-sm text-muted-foreground">投机运势</div>
            <div className="font-bold text-xl text-orange-500">{analysis.cryptoScore}/10</div>
          </div>
          <div className="bg-background/50 rounded-lg p-3 text-center">
            <div className="text-sm text-muted-foreground">暴富流年</div>
            <div className="font-bold text-orange-500">{analysis.cryptoYear}</div>
          </div>
          <div className="bg-background/50 rounded-lg p-3 text-center">
            <div className="text-sm text-muted-foreground">适合流派</div>
            <div className="font-bold text-orange-500">{analysis.cryptoStyle}</div>
          </div>
        </div>
      </section>
      
      {/* Score Legend */}
      <section className="bg-muted/30 rounded-xl p-4">
        <h4 className="text-sm font-semibold mb-3">评分说明</h4>
        <ScoreLegend />
        <p className="text-xs text-muted-foreground mt-3">
          注：命运还受环境和个人选择影响，八字趋势不能完全代表真实人生。命理学不是玄学，
          而是帮助我们在人生列车上做出更好选择的哲学工具。
          一命二运三风水，四积阴德五读书，六名七相八敬神，九遇贵人十养生。
        </p>
      </section>
      
      {/* Yearly Details Table */}
      <section>
        <h3 className="text-lg font-semibold mb-4">流年详批全表</h3>
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">年龄</th>
                <th className="px-3 py-2 text-left font-semibold">年份</th>
                <th className="px-3 py-2 text-left font-semibold">大运</th>
                <th className="px-3 py-2 text-left font-semibold">流年</th>
                <th className="px-3 py-2 text-left font-semibold">评分</th>
                <th className="px-3 py-2 text-left font-semibold">详批</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map((point, idx) => {
                const isUp = point.close >= point.open;
                return (
                  <tr 
                    key={idx} 
                    className={`border-t border-border ${idx % 2 === 0 ? "bg-background" : "bg-muted/30"}`}
                  >
                    <td className="px-3 py-2">{point.age}岁</td>
                    <td className="px-3 py-2">{point.year}</td>
                    <td className="px-3 py-2 text-violet-600 font-medium">{point.daYun || "-"}</td>
                    <td className="px-3 py-2">{point.ganZhi}</td>
                    <td className={`px-3 py-2 font-semibold ${isUp ? "text-green-600" : "text-red-600"}`}>
                      {point.score}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground max-w-xs truncate">{point.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
